import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../../services/authService";
import { useNavigate } from "react-router-dom";
import "../../styles/AdminProducts.css";

const emptyForm = {
  name: "",
  slug: "",
  sku: "",
  brand: "",
  description: "",
  price: "",
  stock: "",
  active: true,
  specs: [],
};
export default function AdminProducts() {
  const { t } = useTranslation();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [currentImages, setCurrentImages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    const response = await fetch("/api/admin/products", {
      credentials: "include",
    });

    if (response.ok) {
      setProducts(await response.json());
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setSelectedImages([]);
    setFileInputKey(Date.now());
    setCurrentImages([]);
  };

  const saveProduct = async (e) => {
    e.preventDefault();

    const csrf = await getCsrfToken();

    const payload = {
      ...form,
      price: Number(form.price),
      stock: Math.max(0, Number(form.stock || 0)),
    };

    const response = await fetch(
      editingId ? `/api/admin/products/${editingId}` : "/api/admin/products",
      {
        method: editingId ? "PUT" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [csrf.headerName]: csrf.token,
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.ok) {
        const saved = await response.json();

        if (selectedImages.length > 0) {
            const formData = new FormData();

            selectedImages.forEach((image) => {
            formData.append("images", image);
            });

            await fetch(`/api/admin/products/${saved.id}/images`, {
            method: "POST",
            credentials: "include",
            headers: {
                [csrf.headerName]: csrf.token,
            },
            body: formData,
            });
        }

        await loadProducts();
        resetForm();
    }
  };

  const editProduct = (product) => {
    setEditingId(product.id);
    setCurrentImages(product.images ?? []);

    setForm({
      name: product.name ?? "",
      slug: product.slug ?? "",
      description: product.description ?? "",
      price: product.price ?? "",
      stock: product.stock ?? "",
      active: Boolean(product.active),
      sku: product.sku ?? "",
      brand: product.brand ?? "",
      specs: product.specs ?? [],
    });
  };

  const toggleProduct = async (id) => {
    const csrf = await getCsrfToken();

    const response = await fetch(`/api/admin/products/${id}/toggle-active`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      const updated = await response.json();

      setProducts((prev) =>
        prev.map((product) => (product.id === updated.id ? updated : product))
      );
    }
  };

  const deleteProduct = async (id) => {
    const csrf = await getCsrfToken();

    const response = await fetch(`/api/admin/products/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      setProducts((prev) => prev.filter((product) => product.id !== id));

      if (editingId === id) {
        resetForm();
      }
    }
  };

  const filteredProducts = products.filter((product) =>
    `${product.name ?? ""} ${product.slug ?? ""} ${product.description ?? ""}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const addSpec = () => {
    setForm((prev) => ({
      ...prev,
      specs: [...prev.specs, { name: "", value: "" }],
    }));
  };

  const updateSpec = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      specs: prev.specs.map((spec, i) =>
        i === index ? { ...spec, [field]: value } : spec
      ),
    }));
  };

  const removeSpec = (index) => {
    setForm((prev) => ({
      ...prev,
      specs: prev.specs.filter((_, i) => i !== index),
    }));
  };

  const deleteProductImage = async (imageId) => {
    const csrf = await getCsrfToken();

    const response = await fetch(`/api/admin/products/images/${imageId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      setCurrentImages((prev) => prev.filter((img) => img.id !== imageId));
      await loadProducts();
    }
  };

  return (
    <main className="admin-page">
      <section className="admin-products">
        <button
          className="admin-back-btn"
          onClick={() => navigate("/admin")}
        >
          ← {t("adminProducts.backToDashboard")}
        </button>
        <h1>{t("adminProducts.title")}</h1>

        <form className="admin-product-form" onSubmit={saveProduct}>
          <h2>
            {editingId
              ? t("adminProducts.editTitle")
              : t("adminProducts.createTitle")}
          </h2>

          <div className="admin-product-form-grid">
            <input
              type="text"
              name="name"
              placeholder={t("adminProducts.name")}
              value={form.name}
              onChange={handleChange}
              required
            />

            <input
              type="text"
              name="slug"
              placeholder={t("adminProducts.slug")}
              value={form.slug}
              onChange={handleChange}
              required
            />

            <input
              type="text"
              name="sku"
              placeholder={t("adminProducts.sku")}
              value={form.sku}
              onChange={handleChange}
            />

            <input
              type="text"
              name="brand"
              placeholder={t("adminProducts.brand")}
              value={form.brand}
              onChange={handleChange}
            />

            <input
              type="number"
              name="price"
              placeholder={t("adminProducts.price")}
              value={form.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              required
            />

            <input
              type="number"
              name="stock"
              placeholder={t("adminProducts.stock")}
              value={form.stock}
              onChange={handleChange}
              min="0"
              required
            />

            <input
                key={fileInputKey}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => setSelectedImages(Array.from(e.target.files))}
            />

            {editingId && currentImages.length > 0 && (
              <div className="admin-product-images-manager">
                {currentImages.map((image) => (
                  <div className="admin-product-image-item" key={image.id}>
                    <img src={image.imageUrl || image.url} alt="Producto" />

                    <button
                      type="button"
                      className="danger-btn"
                      onClick={() => deleteProductImage(image.id)}
                    >
                      Eliminar
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="admin-product-checkbox">
              <input
                type="checkbox"
                name="active"
                checked={form.active}
                onChange={handleChange}
              />
              {t("adminProducts.active")}
            </label>
          </div>

          <textarea
            name="description"
            placeholder={t("adminProducts.description")}
            value={form.description}
            onChange={handleChange}
          />

          <div className="admin-product-specs">
            <div className="admin-product-specs-header">
              <h3>{t("adminProducts.specs")}</h3>

              <button type="button" onClick={addSpec}>
                {t("adminProducts.addSpec")}
              </button>
            </div>

            {form.specs.map((spec, index) => (
              <div className="admin-product-spec-row" key={index}>
                <input
                  type="text"
                  placeholder={t("adminProducts.specName")}
                  value={spec.name}
                  onChange={(e) => updateSpec(index, "name", e.target.value)}
                />

                <input
                  type="text"
                  placeholder={t("adminProducts.specValue")}
                  value={spec.value}
                  onChange={(e) => updateSpec(index, "value", e.target.value)}
                />

                <button type="button" onClick={() => removeSpec(index)}>
                  {t("adminProducts.removeSpec")}
                </button>
              </div>
            ))}
          </div>

          <div className="admin-product-actions">
            <button type="submit">
              {editingId ? t("adminProducts.save") : t("adminProducts.create")}
            </button>

            {editingId && (
              <button type="button" onClick={resetForm}>
                {t("adminProducts.cancel")}
              </button>
            )}
          </div>
        </form>

        <div className="admin-toolbar">
          <input
            type="text"
            placeholder={t("adminProducts.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="admin-products-list">
          {filteredProducts.length === 0 ? (
            <div className="admin-empty">{t("adminProducts.empty")}</div>
          ) : (
            filteredProducts.map((product) => (
              <article key={product.id} className="admin-product-card">
                {product.imageUrl && (
                  <img src={product.imageUrl} alt={product.name} />
                )}

                <div className="admin-product-info">
                  <h2>{product.name}</h2>
                  <p>{product.description}</p>

                  <div className="admin-product-meta">
                    <span>{product.price} €</span>
                    <span
                      className={
                        product.stock === 0
                          ? "stock-empty"
                          : product.stock <= 5
                          ? "stock-low"
                          : "stock-ok"
                      }
                    >
                      {t("adminProducts.stock")}: {product.stock}
                    </span>
                    <span
                      className={
                        product.active ? "status-active" : "status-disabled"
                      }
                    >
                      {product.active
                        ? t("adminProducts.active")
                        : t("adminProducts.inactive")}
                    </span>
                  </div>
                </div>

                <div className="admin-product-card-actions">
                  <button onClick={() => editProduct(product)}>
                    {t("adminProducts.edit")}
                  </button>

                  <button onClick={() => toggleProduct(product.id)}>
                    {product.active
                      ? t("adminProducts.disable")
                      : t("adminProducts.enable")}
                  </button>

                  <button
                    className="danger-btn"
                    onClick={() => deleteProduct(product.id)}
                  >
                    {t("adminProducts.delete")}
                  </button>
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </main>
  );
}